<script>
  import { createEventDispatcher } from 'svelte';
  import { X, Users, Plus, Edit2, Trash2 } from 'lucide-svelte';
  import { customerPricesApi, customersApi, variantConditionPricesApi } from '../stores/api.js';
  import { _ } from '../lib/i18n.js';

  export let show = false;
  export let variant = null;

  let loading = false;
  let error = '';
  let customerPrices = [];
  let customers = [];
  let showAddForm = false;
  let editingCustomerId = null;

  const PRICE_CONDITIONS = ['base', 'weekend', 'holiday', 'urgent', 'night'];
  const CONDITIONS = ['weekend', 'holiday', 'urgent', 'night'];
  const HOURLY_UNITS = ['h', 'ora', 'ore', 'hour', 'hours'];
  // Exact text the backend returns for a duplicate (customer, variant, condition) row (409).
  const CUSTOMER_PRICE_CONFLICT_MESSAGE = 'A custom price already exists for this customer, variant and condition';

  /** Catalogue surcharge for each condition, keyed by conditionCode. Used as placeholders. */
  let catalogPrices = {};

  /**
   * Existing customer price row for the customer currently being added/edited,
   * keyed by conditionCode. This is the source of truth for create-vs-update:
   * it is seeded when the form opens and then updated in place as each write
   * in savePrices() succeeds, so a retry after a partial failure (e.g. one bad
   * condition among five) never re-sends a create for a row that already
   * landed on the server.
   */
  let existingByCondition = {};
  /** customerId that existingByCondition currently reflects, so switching the
   * customer dropdown mid-flow can't reuse another customer's bookkeeping. */
  let existingByConditionCustomerId = null;

  // Form fields
  let formData = {
    customerId: '',
    notes: '',
    prices: { base: '', weekend: '', holiday: '', urgent: '', night: '' }
  };

  const dispatch = createEventDispatcher();

  /**
   * @param {string|null|undefined} unit
   * @returns {boolean}
   */
  function isHourly(unit) {
    return !!unit && HOURLY_UNITS.includes(String(unit).trim().toLowerCase());
  }

  // Load data when modal opens
  $: if (show && variant) {
    loadData();
  }

  async function loadData() {
    try {
      loading = true;
      error = '';

      // Load custom prices for this variant
      const pricesData = await customerPricesApi.getByVariant(variant.id);
      customerPrices = pricesData;
      console.log('📋 Loaded customer prices:', customerPrices);

      // Load all customers for the dropdown
      const customersData = await customersApi.getAll();
      customers = customersData.customers || customersData;
      console.log('👥 Loaded customers:', customers.length);

    } catch (err) {
      error = err.message;
      console.error('❌ Load data error:', err);
    } finally {
      loading = false;
    }
    await loadCatalogPrices();
  }

  /**
   * Re-reads this variant's customer prices from the server without touching
   * `error` or the customer list. Used after a partial save failure so the
   * table reflects what actually landed, even though the form stays open
   * (with its own error message) for the user to fix and retry.
   */
  async function refreshCustomerPrices() {
    try {
      customerPrices = await customerPricesApi.getByVariant(variant.id);
    } catch (err) {
      console.error('❌ Failed to refresh customer prices after a partial save:', err);
    }
  }

  /**
   * Loads the catalogue hourly surcharges for the variant, used as placeholders
   * so an empty customer field visibly means "inherit the catalogue price".
   */
  async function loadCatalogPrices() {
    if (!variant?.id) {
      catalogPrices = {};
      return;
    }
    try {
      const rows = await variantConditionPricesApi.getByVariant(variant.id);
      const map = {};
      for (const row of rows) {
        map[row.conditionCode] = row.price;
      }
      catalogPrices = map;
    } catch (err) {
      console.error('❌ Failed to load catalogue condition prices:', err);
      catalogPrices = {};
      error = $_('products.variants.conditionPrices.loadError');
    }
  }

  /**
   * Groups the flat list of customer price rows by customer, one entry per
   * condition. Rows without a conditionCode are treated as 'base'.
   */
  function groupPricesByCustomer(prices) {
    const groups = new Map();
    for (const price of prices) {
      const conditionCode = price.conditionCode || 'base';
      if (!groups.has(price.customerId)) {
        groups.set(price.customerId, {
          customerId: price.customerId,
          customer: price.customer,
          conditions: {}
        });
      }
      groups.get(price.customerId).conditions[conditionCode] = price;
    }
    return Array.from(groups.values());
  }

  $: groupedPrices = groupPricesByCustomer(customerPrices);

  function resetForm() {
    formData = {
      customerId: '',
      notes: '',
      prices: { base: '', weekend: '', holiday: '', urgent: '', night: '' }
    };
    existingByCondition = {};
    existingByConditionCustomerId = null;
    showAddForm = false;
    editingCustomerId = null;
  }

  function handleClose() {
    if (loading) return;
    show = false;
    resetForm();
    dispatch('close');
  }

  function handleKeydown(event) {
    if (event.key === 'Escape' && !loading) {
      handleClose();
    }
  }

  function startEdit(group) {
    editingCustomerId = group.customerId;
    const prices = { base: '', weekend: '', holiday: '', urgent: '', night: '' };
    for (const condition of PRICE_CONDITIONS) {
      const row = group.conditions[condition];
      if (row) prices[condition] = String(row.customPrice);
    }
    formData = { customerId: group.customerId, notes: group.conditions.base?.notes || '', prices };
    existingByCondition = { ...group.conditions };
    existingByConditionCustomerId = group.customerId;
    showAddForm = true;
  }

  function cancelEdit() {
    resetForm();
  }

  // Get available customers (not already having a custom price)
  function getAvailableCustomers() {
    const existingCustomerIds = groupedPrices.map(g => g.customerId);
    // When editing, include the current customer
    if (editingCustomerId) {
      return customers.filter(c =>
        !existingCustomerIds.includes(c.id) || c.id === editingCustomerId
      );
    }
    return customers.filter(c => !existingCustomerIds.includes(c.id));
  }

  /**
   * Builds the map of existing customer price rows for one customer, keyed by
   * conditionCode, so savePrices() knows whether to create, update or delete.
   */
  function computeExistingByCondition(customerId) {
    const map = {};
    for (const price of customerPrices) {
      if (price.customerId === customerId) {
        map[price.conditionCode || 'base'] = price;
      }
    }
    return map;
  }

  /**
   * Persists one row per filled condition. A cleared field deletes its row, so
   * the customer falls back to the catalogue surcharge. Notes only ever live
   * on the base row; condition rows carry no notes of their own.
   *
   * `existingByCondition` is updated in place as each write succeeds (id
   * adopted on create, value patched on update, entry dropped on delete), and
   * an unchanged value is never re-sent as an update. If one condition fails,
   * the loop stops there but the earlier successes stay recorded, so a retry
   * on the still-open form only re-attempts what actually failed.
   */
  async function savePrices() {
    for (const condition of PRICE_CONDITIONS) {
      const raw = formData.prices[condition];
      const existing = existingByCondition[condition];

      if (raw === '' || raw === null || raw === undefined) {
        if (existing) {
          try {
            await customerPricesApi.delete(existing.id);
            delete existingByCondition[condition];
          } catch (err) {
            console.error('❌ Customer price delete failed', err);
            error = err.message || $_('products.variants.conditionPrices.deleteError');
            return;
          }
        }
        continue;
      }

      const price = parseFloat(String(raw).replace(',', '.'));
      if (!Number.isFinite(price) || price < 0) {
        error = $_('products.variants.conditionPrices.saveError');
        return;
      }
      const notes = condition === 'base' ? (formData.notes || null) : undefined;

      if (existing) {
        // customPrice is DECIMAL server-side: node-postgres returns it as a
        // string (e.g. '42.00'), while `price` is a parseFloat result, so the
        // comparison must go through Number() on both sides or it never
        // matches. Base also compares notes: it is the only row where a
        // no-op price could still hide a real edit (a notes-only change).
        const unchanged = Number(existing.customPrice) === price
          && (condition !== 'base' || (existing.notes || null) === notes);
        if (unchanged) continue;

        try {
          const updateData = { customPrice: price };
          if (condition === 'base') updateData.notes = notes;
          await customerPricesApi.update(existing.id, updateData);
          existingByCondition[condition] = { ...existing, ...updateData };
        } catch (err) {
          console.error('❌ Customer price update failed', err);
          error = err.message || $_('products.variants.conditionPrices.saveError');
          return;
        }
      } else {
        const createData = {
          customerId: formData.customerId,
          variantId: variant.id,
          conditionCode: condition,
          customPrice: price
        };
        if (condition === 'base') createData.notes = notes;

        try {
          const created = await customerPricesApi.create(createData);
          existingByCondition[condition] = created;
        } catch (err) {
          if (err.message === CUSTOMER_PRICE_CONFLICT_MESSAGE) {
            // A row for this (customer, variant, condition) already exists
            // server-side (a previous partial submission, or a second tab).
            // Adopt the real row and apply the pending value as an update
            // instead of leaving the user's edit unsaved.
            try {
              const rows = await customerPricesApi.getByVariant(variant.id);
              const match = rows.find(row =>
                row.customerId === formData.customerId && (row.conditionCode || 'base') === condition
              );
              if (match) {
                const updateData = { customPrice: price };
                if (condition === 'base') updateData.notes = notes;
                await customerPricesApi.update(match.id, updateData);
                existingByCondition[condition] = { ...match, ...updateData };
                continue;
              }
            } catch (retryErr) {
              console.error('❌ Customer price conflict recovery failed', retryErr);
            }
          }
          console.error('❌ Customer price save failed', err);
          error = err.message || $_('products.variants.conditionPrices.saveError');
          return;
        }
      }
    }
  }

  async function handleSubmit(event) {
    event.preventDefault();

    if (!editingCustomerId && !formData.customerId) {
      error = 'Please select a customer';
      return;
    }

    try {
      loading = true;
      error = '';

      // Only re-derive the bookkeeping from the (possibly stale) customerPrices
      // list when the target customer actually changed. A retry for the same
      // customer after a partial failure must keep the live-updated map from
      // the previous attempt, or a re-sent create would 409 on rows that
      // already landed.
      if (existingByConditionCustomerId !== formData.customerId) {
        existingByCondition = computeExistingByCondition(formData.customerId);
        existingByConditionCustomerId = formData.customerId;
      }

      await savePrices();
      if (error) {
        // Some conditions may have been written before the one that failed;
        // refresh the list so the table reflects reality while the form
        // stays open (with its error) for the user to fix and resubmit.
        await refreshCustomerPrices();
        return;
      }

      console.log('✅ Customer prices saved');

      // Reload data
      await loadData();
      resetForm();
      dispatch('success');

    } catch (err) {
      error = err.message;
      console.error('❌ Save error:', err);
      await refreshCustomerPrices();
    } finally {
      loading = false;
    }
  }

  async function deleteGroup(group) {
    if (!confirm('Are you sure you want to delete this customer\'s custom prices?')) {
      return;
    }

    try {
      loading = true;
      error = '';
      for (const row of Object.values(group.conditions)) {
        await customerPricesApi.delete(row.id);
      }
      console.log('🗑️ Customer prices deleted');
      // The rows just deleted can no longer back a create-vs-update decision.
      // If the open form was targeting this customer, its cached ids are now
      // dead: reset it rather than let a resubmit update()/skip a row that no
      // longer exists.
      if (existingByConditionCustomerId === group.customerId) {
        resetForm();
      }
      await loadData();
      dispatch('success');
    } catch (err) {
      error = err.message;
      console.error('❌ Delete error:', err);
    } finally {
      loading = false;
    }
  }

  function formatPrice(price) {
    if (price === null || price === undefined) return '-';
    const numPrice = typeof price === 'string' ? parseFloat(price) : price;
    if (isNaN(numPrice)) return '-';
    return `€${numPrice.toFixed(2)}`;
  }

  function placeholderFor(condition) {
    if (condition === 'base') {
      return variant?.price !== null && variant?.price !== undefined ? String(variant.price) : '0.00';
    }
    const catalogPrice = catalogPrices[condition];
    return catalogPrice !== null && catalogPrice !== undefined ? String(catalogPrice) : '0.00';
  }
</script>

<svelte:window on:keydown={handleKeydown} />

{#if show && variant}
  <!-- Modal backdrop -->
  <div class="modal-backdrop fade-in">
    <!-- Modal -->
    <div class="modal-content max-w-4xl w-full scale-in">
      <!-- Header -->
      <div class="px-6 py-5 border-b border-gray-200/60 dark:border-gray-700 flex items-center justify-between bg-linear-to-r from-primary-50/50 dark:from-primary-900/30 to-transparent">
        <div class="flex items-center space-x-3">
          <div class="w-10 h-10 bg-linear-to-br from-primary-500 to-primary-600 rounded-xl flex items-center justify-center">
            <Users class="h-5 w-5 text-white" />
          </div>
          <div>
            <h2 class="text-lg font-bold text-gray-900 dark:text-white">
              Custom Prices
            </h2>
            <p class="text-sm text-gray-500 dark:text-gray-400">
              {variant.code} - {variant.name} (Base: {formatPrice(variant.price)})
            </p>
          </div>
        </div>
        {#if !loading}
          <button
            onclick={handleClose}
            class="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-xl transition-all duration-200"
          >
            <X size={20} />
          </button>
        {/if}
      </div>

      <!-- Content -->
      <div class="px-6 py-4 max-h-[60vh] overflow-y-auto">
        {#if error}
          <div class="mb-4 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-md p-3">
            <div class="text-sm text-red-800 dark:text-red-200">{error}</div>
          </div>
        {/if}

        {#if loading && customerPrices.length === 0}
          <div class="text-center py-8">
            <div class="loading-spinner h-8 w-8 mx-auto mb-2"></div>
            <p class="text-sm text-gray-500 dark:text-gray-400">Loading...</p>
          </div>
        {:else}
          <!-- Customer Prices Table -->
          {#if groupedPrices.length > 0}
            <div class="overflow-x-auto mb-4">
              <table class="w-full">
                <thead class="bg-gray-50 dark:bg-gray-700/50">
                  <tr>
                    <th class="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">Customer</th>
                    <th class="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">{$_('products.variants.conditionPrices.base')}</th>
                    {#if isHourly(variant.unit)}
                      {#each CONDITIONS as condition}
                        <th class="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">{$_(`products.variants.conditionPrices.${condition}`)}</th>
                      {/each}
                    {/if}
                    <th class="px-4 py-3 text-right text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody class="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                  {#each groupedPrices as group}
                    <tr class="hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors">
                      <td class="px-4 py-3">
                        <div>
                          <span class="text-sm font-medium text-gray-900 dark:text-white">
                            {group.customer?.companyName || group.customerId}
                          </span>
                          <span class="text-xs text-gray-500 dark:text-gray-400 ml-2">
                            ({group.customerId})
                          </span>
                        </div>
                      </td>
                      <td class="px-4 py-3">
                        <span class="text-sm font-semibold text-primary-600 dark:text-primary-400">
                          {formatPrice(group.conditions.base?.customPrice)}
                        </span>
                        {#if variant.price && group.conditions.base}
                          <span class="text-xs text-gray-500 dark:text-gray-400 ml-2">
                            ({((1 - group.conditions.base.customPrice / variant.price) * 100).toFixed(0)}% off)
                          </span>
                        {/if}
                        {#if group.conditions.base?.notes}
                          <div class="text-xs text-gray-500 dark:text-gray-400 mt-1">{group.conditions.base.notes}</div>
                        {/if}
                      </td>
                      {#if isHourly(variant.unit)}
                        {#each CONDITIONS as condition}
                          <td class="px-4 py-3">
                            <span class="text-sm text-gray-600 dark:text-gray-400">
                              {formatPrice(group.conditions[condition]?.customPrice)}
                            </span>
                          </td>
                        {/each}
                      {/if}
                      <td class="px-4 py-3 text-right">
                        <div class="flex items-center justify-end space-x-2">
                          <button
                            onclick={() => startEdit(group)}
                            class="p-1.5 text-gray-500 hover:text-primary-600 dark:text-gray-400 dark:hover:text-primary-400 hover:bg-gray-100 dark:hover:bg-gray-600 rounded transition-colors"
                            title="Edit"
                            disabled={loading}
                          >
                            <Edit2 size={14} />
                          </button>
                          <button
                            onclick={() => deleteGroup(group)}
                            class="p-1.5 text-gray-500 hover:text-red-600 dark:text-gray-400 dark:hover:text-red-400 hover:bg-gray-100 dark:hover:bg-gray-600 rounded transition-colors"
                            title="Delete"
                            disabled={loading}
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  {/each}
                </tbody>
              </table>
            </div>
          {:else if !showAddForm}
            <div class="text-center py-8 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg mb-4">
              <Users size={32} class="mx-auto text-gray-400 dark:text-gray-500 mb-2" />
              <p class="text-sm text-gray-500 dark:text-gray-400">No custom prices for this variant</p>
            </div>
          {/if}

          <!-- Add/Edit Form -->
          {#if showAddForm}
            <div class="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
              <h3 class="text-sm font-semibold text-gray-900 dark:text-white mb-3">
                {editingCustomerId ? 'Edit Custom Price' : 'Add Custom Price'}
              </h3>
              <form onsubmit={handleSubmit}>
                <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <!-- Customer Select -->
                  <div>
                    <label for="customer" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Customer *
                    </label>
                    {#if editingCustomerId}
                      <input
                        type="text"
                        value={groupedPrices.find(g => g.customerId === editingCustomerId)?.customer?.companyName || formData.customerId}
                        class="input w-full bg-gray-100 dark:bg-gray-700"
                        disabled
                      />
                    {:else}
                      <select
                        id="customer"
                        bind:value={formData.customerId}
                        class="input w-full"
                        disabled={loading}
                        required
                      >
                        <option value="">Select customer...</option>
                        {#each getAvailableCustomers() as customer}
                          <option value={customer.id}>
                            {customer.companyName || customer.name || customer.id}
                          </option>
                        {/each}
                      </select>
                    {/if}
                  </div>

                  <!-- Base Price -->
                  <div>
                    <label for="price-base" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      {$_('products.variants.conditionPrices.base')}
                    </label>
                    <div class="relative">
                      <span class="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 dark:text-gray-400">€</span>
                      <input
                        id="price-base"
                        type="text"
                        bind:value={formData.prices.base}
                        placeholder={placeholderFor('base')}
                        class="input w-full pl-8"
                        disabled={loading}
                      />
                    </div>
                  </div>

                  <!-- Notes (attached to the base row) -->
                  <div>
                    <label for="notes" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Notes
                    </label>
                    <input
                      id="notes"
                      type="text"
                      bind:value={formData.notes}
                      placeholder="e.g. VIP client"
                      class="input w-full"
                      disabled={loading}
                    />
                  </div>
                </div>

                {#if isHourly(variant.unit)}
                  <div class="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                    <p class="text-xs text-gray-500 dark:text-gray-400 mb-2">{$_('products.variants.conditionPrices.inheritHint')}</p>
                    <div class="grid grid-cols-2 md:grid-cols-4 gap-3">
                      {#each CONDITIONS as condition}
                        <label class="block" for={`price-${condition}`}>
                          <span class="text-xs text-gray-600 dark:text-gray-400">{$_(`products.variants.conditionPrices.${condition}`)}</span>
                          <div class="relative">
                            <span class="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 dark:text-gray-400">€</span>
                            <input
                              id={`price-${condition}`}
                              type="text"
                              bind:value={formData.prices[condition]}
                              placeholder={placeholderFor(condition)}
                              class="input w-full pl-8"
                              disabled={loading}
                            />
                          </div>
                        </label>
                      {/each}
                    </div>
                  </div>
                {/if}

                <div class="flex justify-end space-x-2 mt-4">
                  <button
                    type="button"
                    onclick={cancelEdit}
                    class="btn btn-secondary"
                    disabled={loading}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    class="btn btn-primary"
                    disabled={loading}
                  >
                    {#if loading}
                      <svg class="animate-spin -ml-1 mr-2 h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                    {/if}
                    {editingCustomerId ? 'Update' : 'Save'}
                  </button>
                </div>
              </form>
            </div>
          {:else}
            <!-- Add Button -->
            <button
              onclick={() => { showAddForm = true; }}
              class="inline-flex items-center px-3 py-2 text-sm font-medium text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 hover:bg-primary-50 dark:hover:bg-primary-900/30 rounded-lg transition-colors"
              disabled={loading}
            >
              <Plus size={16} class="mr-1" />
              Add Customer Price
            </button>
          {/if}
        {/if}
      </div>

      <!-- Footer -->
      <div class="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex justify-end">
        <button
          onclick={handleClose}
          class="btn btn-secondary"
          disabled={loading}
        >
          Close
        </button>
      </div>
    </div>
  </div>
{/if}
